<template>
  <div ref='drillDownTarget' :id="id" class="item" :class='klass' :key="key">
    <div v-if="isAnyMeasure" @click='visit()'>
      <span>{{ item.dname }}</span>
      <span v-if="flagText" class="cda-item-flag" data-test="cda-item-flag">{{ flagText }}</span>
      <span style="float:right;clear=right">{{ item.value }} {{ item.units?'('+item.units+')':'' }}</span>
      <!-- <watch-button-measure v-if="isWatchable" class="watch-button-in-cda" :measKind="kind" :procCcd="item.procCcd" :measCcd="item.ccd"/> -->
    </div>
    <div v-else-if="isAnyProcedure" class='accordion procedure w-100' :class='klass' v-on="visitable0 ? { click:visit }:{}">
      <!-- <watch-button-procedure class='watch-button-in-cda' v-if='isProcedure' :procCcd='item.ccd' :procId='item.id'/> -->
      <div class='card'>
        <togglable-caret-header class='card-header' :data-bs-target='dataTarget' :showCaret='isPopulatedAnyProcedure' :class='togglableClass'>
          <a role='button'>{{ item.desc.toLowerCase() }}</a>
          <span v-if="flagText" class="cda-item-flag" data-test="cda-item-flag">{{ flagText }}</span>
          <watch-button-procedure class='watch-button-in-cda' v-if='isProcedure' :procCcd='item.ccd' :procId='item.id'/>
        </togglable-caret-header>
        <div class='collapse' :id='dataTargetId'>
          <div class='card-body'>
            <cda-visitable-item v-for="measure in item.measures" :key="'submeasurekey'+measure.id" :item='measure'>
            </cda-visitable-item>
          </div>
        </div>
        <!-- <watch-button-procedure class='watch-button-in-cda 2' v-if='isProcedure' :procCcd='item.ccd' :procId='item.id'/> -->
      </div>
    </div>
    <div v-else class="import-selectable-row" @click='visit()'>
      <input
        v-if="isImportSelectable"
        type="checkbox"
        class="cda-import-checkbox"
        :checked="importSelected"
        @click.stop
        @change="onImportToggle"
      />
      <span v-if='mayHaveSubstance'>{{ item.substance || item.desc }}</span>
      <span v-else>{{ item.desc }}</span>
      <span v-if="flagText" class="cda-item-flag" data-test="cda-item-flag">{{ flagText }}</span>
    </div>
  </div>
</template>
<script>
import $ from 'jquery';
import WatchButtonMeasure from '@/components/private-layouts/_partials/watch/WatchButtonMeasure';
import WatchButtonProcedure from '@/components/private-layouts/_partials/watch/WatchButtonProcedure';
import kinds from '@/constants/kinds';
import types from '@/data/types';
import { hasMeasures,isOfficeVisit } from '@/data/getters';
import { mapGetters } from 'vuex';
import TogglableCaretHeader from '@/components/includes/TogglableCaretHeader';
import { findCdaNarrativeElement } from '@/utils/cdaNarrative';

export default {
  name: 'cda-visitable-item',
  props: {
    item: null,
    /**
     * The item is ticked for "Move selected ... to Current". The side bar holds
     * what is ticked, so the box is drawn from it: an item the list moves to
     * another group is a new box here.
     */
    importSelected: { type: Boolean, default: false },
  },
  watch: {
    ready(newReady) {
      if(newReady && this.matches) {
        this.drillDownTarget.scrollIntoView({block:'center'});
        this.visit();
      }
    }
  },
  data() {
    return {
      kinds,
      types
    }
  },
  components: {
    'watch-button-measure': WatchButtonMeasure,
    'watch-button-procedure': WatchButtonProcedure,
    'togglable-caret-header': TogglableCaretHeader
  },
  computed: {
    ...mapGetters('login', {
      allData: 'getAllData'
    }),
    dataTargetId() {
      return 'measures-target-' + this.item.id;
    },
    dataTarget() {
      return '#'+ this.dataTargetId;
    },
    togglableClass() {
      return {
        'office-visit' : isOfficeVisit(this.item,this.allData)
      };
    },
    kind() {
      return this.item.kind;
    },
    isPopulatedProcedure() {
      return this.kind === 'procedure' && hasMeasures(this.item.id,this.allData);
    },
    isPopulatedEditableProcedure() {
      return this.kind === 'eprocedure' && hasMeasures(this.item.id,this.allData,this.item.cdaIndex);
    },
    isPopulatedAnyProcedure() {
      // Pre-populated measures (lab panels moved to Labs bucket) still expand.
      return this.isPopulatedProcedure
        || this.isPopulatedEditableProcedure
        || (this.isAnyProcedure && Array.isArray(this.item.measures) && this.item.measures.length > 0);
    },
    isProcedure() {
      return this.kind === this.kinds.PROCEDURE;
    },
    isEditableProcedure() {
      return this.kind === this.kinds.E_PROCEDURE;
    },
    isAnyProcedure() {
      return this.isProcedure || this.isEditableProcedure;
    },
    isAnyDiagnosis() {
      return this.kind === this.kinds.E_DIAGNOSIS || this.kind === this.kinds.DIAGNOSIS;
    },
    isAnyMedication() {
      return this.kind === this.kinds.E_MEDICATION || this.kind === this.kinds.MEDICATION;
    },
    isImportSelectable() {
      return this.isAnyDiagnosis || this.isAnyMedication;
    },
    isAnyAllergy() {
      return this.kind === this.kinds.E_ALLERGY || this.kind === this.kinds.ALLERGY;
    },
    /**
     * Without an allergen code the row is named by its substance - the narrative's
     * name for what the patient is allergic to - because the code it falls back
     * to is the allergy's type, not the substance's (main shows it the same way).
     */
    mayHaveSubstance() {
      return (this.isAnyAllergy || this.isAnyDiagnosis) && !this.item.hasallergencode;
    },
    /**
     * What the review notes about this item, in words: the document gave it no
     * date, or no code this system knows. Notes, not blockers - Save still saves
     * it, as it stands (CdaStagedItemNotes has the controls).
     */
    flagText() {
      const words = { missing_date: 'no date', uncoded: 'no code' };
      const flags = Array.isArray(this.item.flags) ? this.item.flags : [];

      return flags.map((flag) => words[flag] || flag).join(', ');
    },
    isWatchable() {
      return this.isMeasure || this.isPopulatedProcedure;
    },
    isMeasure() {
      return this.kind === 'measures';
    },
    isEditableMeasure() {
      return this.kind === 'emeasure';
    },
    isAnyMeasure() {
      return this.isMeasure || this.isEditableMeasure;
    },
    id() {
      return this.item.htmlname;
    },
    klass() {
      return '' +
        (this.visitable ? " clickable":"") +
        (this.selected ? " selected":"");
    },
    key() {
      let prefix = this.isParentVisitableItem ? 'cda-visitable-subitem' : 'cda-visitable-item';
      return prefix+this.kind+this.item.id;
    },
    isParentVisitableItem() {
       return this.$parent.$options.name === 'cda-visitable-item';
    },
    drillDownTarget() {
      return this.$refs.drillDownTarget;
    },
    visitable0() {
      let result =  this.visitable;
      let result0 = Boolean(this.item.htmlname && !this.isPopulatedAnyProcedure);
      return result;
    },
    visitable() {
      return Boolean(this.item && this.item.htmlname && !this.isPopulatedAnyProcedure);
    },
    selected() {
      return this.selectedItem === this;
    },
    matches() {
      if(!this.itemInfo)
        return false;
      let itemInfo = this.itemInfo;
      let type = itemInfo.type;
      let kind = this.kind;
      return itemInfo.id === this.item.id && (
          (kind === kinds.VACCINE && type === types.vaccination) ||
          (kind === kinds.DIAGNOSIS && type === types.diagnosis) ||
          (kind === kinds.ALLERGY && type === types.allergy) ||
          (kind === kinds.MEDICATION && type === types.medication));
    },
    itemInfo() {
      return this.$parent.itemInfo;
    },
    ready() {
      return this.$parent.ready;
    },
    selectedItem() {
      return this.$parent.selectedItem;
    }
  },
  methods: {
    onImportToggle(event) {
      this.$emit('toggle-cda-selection', {
        kind: this.kind,
        item: this.item,
        checked: event.target.checked
      });
    },
    visit() {
      let iframe = $('iframe');
      let iframeContents = iframe.contents();
      let iframeHeight = iframe.height();
      let iframeWindow = iframe[0].contentWindow;
      let eventTarget = this.isParentVisitableItem ? this.$parent : this;

      eventTarget.$emit('selection-changed-event',null);
      eventTarget.$emit('selection-changed-event',this);
      if(!this.visitable)
        return;
      let elementToVisit = findCdaNarrativeElement(iframeContents, this.item.htmlname);
      let elementOffset = elementToVisit.length ? elementToVisit.offset() : null;
      if (!elementOffset)
        return;
      let elementToVisitTop = elementOffset.top;
      let elementToVisitHeight = elementToVisit.height();

      // find positions where to scroll
      let horizontalScroll = 0;
      let verticalScroll = elementToVisitTop 
        - iframeHeight / 2 + elementToVisitHeight / 2;

      iframeWindow.scrollTo(horizontalScroll, verticalScroll);     

      const className = 'selected-D18AC1FAF08C41F9A02F173FCFFB59A1';
      iframeContents.find('*').removeClass(className);
      elementToVisit.addClass(className);
    }
  }
}

</script>

<style scoped lang="scss">

$tint: rgba(0,0,0,.2);
$tint2: rgba(0,0,0,.08);

$selected: rgba(255,255,0,1);
$selectedTint: rgba(0,0,255,.15);

.procedure {
    display: inline-flex;

  }
  .card {
    width: 100%;
    padding: 0;
    border: none;
    background-color: transparent;

    .card-header {
      /*padding: 0;*/
      width: 100%;
      border: none;
      background-color: transparent;
      text-transform: capitalize;
      padding: 0.5rem 1.0rem 0.5rem 0;
    }

    .card-body {
      padding: 0;
      background-color: transparent;
    }
    .office-visit {
      opacity: .6;
      font-size: .7rem;
    }
  }

  .item {
    padding: 0 0.5rem;
    width: 100%;
  }
  .import-selectable-row {
    display: flex;
    align-items: flex-start;
    gap: 0.35rem;
    width: 100%;
  }
  .cda-import-checkbox {
    margin-top: 0.2rem;
    flex-shrink: 0;
  }
  .clickable.selected:hover {
    background-color: $selected;
  }
  .selected:not(:hover) {
    background-color: $selectedTint;
    animation-name : fade;
    animation-duration: .3s;
  }
  @keyframes fade {
    from { background-color: $selected; }
    60% { background-color: $selected; }
    to { background-color: $selectedTint; }
  }
  .clickable.selected:not(:hover) {
    background-color:$selectedTint;
  }
  .clickable:not(:hover) {
    background-color : $tint2;
  }
  .clickable:hover {
    background-color : $tint;
  }
  .watch-button-in-cda {
    display: inline-block;
    padding: 0;
  }
  .cda-item-flag {
    margin-left: 0.5rem;
    padding: 0 0.35rem;
    border: 1px solid #f0ad4e;
    border-radius: 0.25rem;
    color: #8a5a00;
    font-size: 0.75rem;
    white-space: nowrap;
  }

</style>
